/**
 * Utility functions for calculating current futures contract symbols
 * Frontend version of tradestation-server/utils/contractSymbols.js
 */

const FUTURES_MONTHS = [
  { code: 'H', month: 2, name: 'March' },
  { code: 'M', month: 5, name: 'June' },
  { code: 'U', month: 8, name: 'September' },
  { code: 'Z', month: 11, name: 'December' }
];

// Base futures products that need contract month/year appended
const BASE_FUTURES_PRODUCTS = [
  'MES', 'MNQ', 'MYM', 'M2K',  // Micro E-mini
  'ES', 'NQ', 'YM', 'RTY',      // E-mini
  'CL', 'MCL', 'NG',            // Energy
  'GC', 'MGC', 'SI', 'MHG',     // Metals
  'ZB', 'ZN', 'ZF', 'ZT',       // Treasury
  'ZC', 'ZS', 'ZW',             // Grains
  '6E', '6A', '6B', '6C', '6J', // Currency (standard notation)
  'M6E', 'M6A', 'M6B'           // Micro currency
];

/** TradeStation continuous quotes use a leading @ (`@MNQ`). Orders need the root or contract. */
export function stripContinuousPrefix(symbol) {
  const upper = String(symbol || '').trim().toUpperCase();
  return upper.startsWith('@') ? upper.slice(1) : upper;
}

export function isBaseFuturesProduct(symbol) {
  const upperSymbol = stripContinuousPrefix(symbol);
  if (!upperSymbol) return false;
  return BASE_FUTURES_PRODUCTS.includes(upperSymbol);
}

/**
 * Check if a symbol is already a futures contract (has month/year)
 * @param {string} symbol - The ticker symbol
 * @returns {boolean}
 */
export function isFuturesContract(symbol) {
  const upper = stripContinuousPrefix(symbol);
  if (!upper) return false;
  // Pattern: base symbol + month code (H,M,U,Z) + 2-digit year
  // Examples: MNQZ25, ESH25, GCM26
  const contractPattern = /^[A-Z0-9]{2,4}[HMUZ]\d{2}$/;
  return contractPattern.test(upper);
}

/**
 * Get the current active contract month for a given date
 * Futures contracts typically expire on the third Friday of the contract month.
 * We roll to the next contract around mid-month (15th) to avoid expiration issues.
 * @param {Date} date - The reference date
 * @returns {Object} - { monthCode, year, monthName }
 */
export function getNextContractMonth(date = new Date()) {
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();
  const currentDay = date.getDate();
  
  // Check if current month is a contract month
  const currentMonthContract = FUTURES_MONTHS.find(m => m.month === currentMonth);
  
  if (currentMonthContract) {
    // If we're in a contract month and before the 15th, use this month's contract
    // Most index futures expire on the third Friday (~15th-21st)
    if (currentDay < 15) {
      return {
        monthCode: currentMonthContract.code,
        year: currentYear,
        monthName: currentMonthContract.name
      };
    }
  }
  
  // Find the next contract month (first month > current)
  let contract = FUTURES_MONTHS.find(m => m.month > currentMonth);
  
  if (!contract) {
    // If no contract found in current year, use next year's first month
    contract = FUTURES_MONTHS[0];
    return {
      monthCode: contract.code,
      year: currentYear + 1,
      monthName: contract.name
    };
  }
  
  return {
    monthCode: contract.code,
    year: currentYear,
    monthName: contract.name
  };
}

/**
 * Generate current contract symbol for a futures product
 * @param {string} product - The futures product (e.g., 'MNQ', 'ES', 'NQ')
 * @param {Date} date - The reference date
 * @returns {string} - The contract symbol (e.g., 'MNQZ25')
 */
export function getCurrentContractSymbol(product, date = new Date()) {
  const { monthCode, year } = getNextContractMonth(date);
  const shortYear = year % 100;
  return `${product}${monthCode}${shortYear.toString().padStart(2, '0')}`;
}

/**
 * Map a quote symbol onto something that can be traded.
 * `@MNQ` and `MNQ` both become the front-month contract (MNQZ26).
 * Full contracts and stocks are unchanged.
 */
export function resolveTradableSymbol(symbol, date = new Date()) {
  const cleaned = stripContinuousPrefix(symbol);
  if (!cleaned) return '';
  if (isBaseFuturesProduct(cleaned)) return getCurrentContractSymbol(cleaned, date);
  return cleaned;
}

/**
 * Get contract month and year info from a contract symbol
 * @param {string} contractSymbol - Full contract symbol (e.g., 'MNQZ25')
 * @returns {Object|null} - { monthCode, year, monthName, baseSymbol } or null if invalid
 */
export function parseContractSymbol(contractSymbol) {
  if (!isFuturesContract(contractSymbol)) return null;
  
  // Extract month code and year from end
  const upperSymbol = contractSymbol.toUpperCase();
  const monthCode = upperSymbol.charAt(upperSymbol.length - 3);
  const yearStr = upperSymbol.slice(-2);
  const year = 2000 + parseInt(yearStr, 10);
  const baseSymbol = upperSymbol.slice(0, -3);
  
  const monthInfo = FUTURES_MONTHS.find(m => m.code === monthCode);
  
  return {
    monthCode,
    year,
    monthName: monthInfo?.name || 'Unknown',
    baseSymbol
  };
}

/**
 * Get a list of common futures products with their current contracts
 * @param {Date} date - The reference date
 * @returns {Array} - Array of { symbol, name, currentContract }
 */
export function getCommonFuturesContracts(date = new Date()) {
  const futures = [
    // Equity index futures (E-mini & Micro E-mini)
    { symbol: 'MES', name: 'Micro E-mini S&P 500' },
    { symbol: 'MNQ', name: 'Micro E-mini Nasdaq-100' },
    { symbol: 'ES',  name: 'E-mini S&P 500' },
    { symbol: 'NQ',  name: 'E-mini Nasdaq-100' },
    { symbol: 'YM',  name: 'E-mini Dow Jones' },
    { symbol: 'MYM', name: 'Micro E-mini Dow Jones' },
    { symbol: 'RTY', name: 'E-mini Russell 2000' },
    { symbol: 'M2K', name: 'Micro E-mini Russell 2000' },

    // Energies
    { symbol: 'CL',  name: 'Crude Oil' },
    { symbol: 'MCL', name: 'Micro WTI Crude Oil' },
    { symbol: 'NG',  name: 'Natural Gas' },

    // Metals
    { symbol: 'GC',  name: 'Gold' },
    { symbol: 'MGC', name: 'Micro Gold' },
    { symbol: 'SI',  name: 'Silver' },
    { symbol: 'MHG', name: 'Micro Copper' },
  ];
  
  return futures.map(future => ({
    ...future,
    currentContract: getCurrentContractSymbol(future.symbol, date)
  }));
}

/**
 * Get all available contract symbols for a product (current + next few)
 * @param {string} product - The futures product
 * @param {number} count - Number of contracts to generate
 * @param {Date} date - The reference date
 * @returns {Array} - Array of contract symbols
 */
export function getContractSeries(product, count = 4, date = new Date()) {
  const contracts = [];
  let currentDate = new Date(date);
  
  for (let i = 0; i < count; i++) {
    contracts.push(getCurrentContractSymbol(product, currentDate));
    
    // Move to next contract month
    const { monthCode, year } = getNextContractMonth(currentDate);
    const nextMonthIndex = FUTURES_MONTHS.find(m => m.code === monthCode).month;
    currentDate = new Date(year, nextMonthIndex, 1);
  }
  
  return contracts;
}

